import { getWindow } from "@faire/web--source/common/globals/getWindow";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import * as React from "react";

import { DROPDOWN_LIST_HEIGHT, ROW_SIZE } from "../constants";
import {
  CustomEditSpan,
  DropdownItem,
  DropdownWrapper,
  EditOptionButton,
  OptionRow,
} from "../styles";
import { ICreateNewOption, IOption } from "../types";

import { CreateNewOptionButton } from "./CreateNewOptionButton";
import { ICustomOption, IOptionMixin } from "./types";
import { getLabel, getValue } from "./utils";

interface DropdownContentProps<T extends string> {
  filteredOptions: IOptionMixin<T>[];
  handleEditCustomOption: (event: React.MouseEvent<HTMLButtonElement>) => void;
  handleSelectDropDownItem: (
    event: React.MouseEvent<HTMLButtonElement>,
    index: number
  ) => void;
  helper?: string;
  highlightIndex: number;
  isCustomOption: (
    maybeCustomOption: IOption | ICustomOption
  ) => maybeCustomOption is ICustomOption;
  onHighlight: (index: number) => () => void;
  open: boolean;
  refs: React.MutableRefObject<Record<number, HTMLButtonElement | null>>;
  showErrorMessage: boolean;
  targetRef: React.MutableRefObject<HTMLInputElement | null>;
  containingElement?: HTMLElement;
  options: IOption<T>[];
  createNewOption?: ICreateNewOption;
  searchValue?: string;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSearchValue: React.Dispatch<React.SetStateAction<string | undefined>>;
}

export const DropdownContent = <T extends string>({
  filteredOptions,
  handleEditCustomOption,
  handleSelectDropDownItem,
  helper,
  highlightIndex,
  isCustomOption,
  onHighlight,
  open,
  refs,
  showErrorMessage,
  targetRef,
  containingElement,
  options,
  createNewOption,
  searchValue,
  setOpen,
  setSearchValue,
}: DropdownContentProps<T>) => {
  // Returns true if the dropdown should open up, false otherwise.
  const calculateIsUp = () => {
    const sizeOfDropdown =
      ROW_SIZE * Math.min(DROPDOWN_LIST_HEIGHT / ROW_SIZE, options.length);
    const potentialBottom = targetRef.current?.getBoundingClientRect().top;
    const potentialTop = targetRef.current?.getBoundingClientRect().bottom;

    if (potentialBottom === undefined || potentialTop === undefined) {
      return false;
    }

    // If we're going to go above the screen, it's better to go down
    if (potentialBottom - sizeOfDropdown < 0) {
      return false;
    }

    return (
      potentialTop + sizeOfDropdown >
      (containingElement?.getBoundingClientRect()?.bottom ??
        getWindow()?.innerHeight ??
        0)
    );
  };

  return (
    <DropdownWrapper
      data-test-id="dropdown_wrapper"
      $showErrorMessage={showErrorMessage}
      $hasHelper={!!helper}
      $isOpen={open}
      $isUp={calculateIsUp()}
      role="listbox"
      aria-multiselectable={false}
    >
      {filteredOptions.map((option, index) => {
        const label = getLabel(option);
        const value = getValue(option);
        const isSelected = index === highlightIndex;
        const isCustom = isCustomOption(option);

        return (
          <OptionRow
            key={`${label} ${index}`}
            $highlight={isSelected}
            align="center"
            onMouseOver={onHighlight(index)}
          >
            <DropdownItem
              role="option"
              ref={(el: HTMLButtonElement | null) => {
                if (!el) {
                  return;
                }
                refs.current[index] = el;
              }}
              tabIndex={-1}
              $isCustom={isCustom && option.hasEdited}
              onClick={
                isCustom
                  ? option.onClick ??
                    (option.hasEdited
                      ? (event) => handleSelectDropDownItem(event, index)
                      : handleEditCustomOption)
                  : undefined
              }
              onMouseDown={
                isCustom
                  ? undefined
                  : (event) => handleSelectDropDownItem(event, index)
              }
              onMouseOver={onHighlight(index)}
              data-test-id={`dropdownItem_${value}`}
              type="button"
              aria-selected={isSelected}
              disabled={option.disabled}
            >
              {label}
            </DropdownItem>
            {isCustom && option.hasEdited ? (
              <EditOptionButton
                onClick={handleEditCustomOption}
                onMouseOver={onHighlight(index)}
                data-test-id={`custom_dropdownItem_${value}`}
                type="button"
                aria-selected={isSelected}
              >
                <CustomEditSpan>
                  <StrictLocalMsg
                    defaultMessage="Edit"
                    description={{
                      text: "A button named Edit the let user know they can edit an option.",
                      img: "https://cdn.faire.com/fastly/e918de51aa45bab72dc3117b6e2bfad3a224a44af020ff47a19466fcaf91b401.png",
                    }}
                  />
                </CustomEditSpan>
              </EditOptionButton>
            ) : null}
          </OptionRow>
        );
      })}

      <CreateNewOptionButton
        createNewOption={createNewOption}
        searchValue={searchValue}
        filteredOptions={filteredOptions}
        highlightIndex={highlightIndex}
        onHighlight={onHighlight}
        setOpen={setOpen}
        setSearchValue={setSearchValue}
        refs={refs}
      />
    </DropdownWrapper>
  );
};
